/**
 * /device?code=ABCD-EFGH — the browser half of the device sign-in flow
 * (backend: routes/cli-auth.ts). A tool on the customer's computer (the
 * Tyashin Claude plugin, a CLI) shows a code; the customer opens this page,
 * signs in or creates a free account, checks the code matches and approves.
 * The tool then receives a revocable access token for THIS account only.
 *
 * Handles its own auth redirect like /invite: signed-out visitors are sent to
 * /login or /register and returned here afterwards.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';
import { useAuthStore } from '@/stores/auth';
import { CheckCircle2, KeyRound, Loader2, LogIn, ShieldX, UserPlus } from 'lucide-react';

interface DeviceRequest {
  userCode: string;
  clientName: string;
  createdAt: string;
  expiresAt: string;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4 dark:bg-gray-950">
      <div className="w-full max-w-md rounded-xl border bg-white p-8 shadow-sm dark:bg-gray-900">
        {children}
      </div>
    </div>
  );
}

export default function DeviceApprovePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, _hasHydrated, user, logout } = useAuthStore();
  const [typed, setTyped] = useState('');
  const code = (params.get('code') || '').toUpperCase();
  const returnTo = { pathname: '/device', search: code ? `?code=${code}` : '' };

  const request = useQuery({
    queryKey: qk.deviceRequest(code),
    queryFn: async () =>
      (await api.get(`/auth/cli/device/${encodeURIComponent(code)}`)).data.data as DeviceRequest,
    enabled: _hasHydrated && isAuthenticated && Boolean(code),
    retry: false,
  });

  const decide = useMutation({
    mutationFn: async (approve: boolean) =>
      (
        await api.post(
          `/auth/cli/device/${encodeURIComponent(code)}/${approve ? 'approve' : 'deny'}`
        )
      ).data.data as { status: 'approved' | 'denied'; clientName: string },
  });

  // An admin or impersonation session cannot approve: offer a way to sign in as
  // the customer instead of a dead end (seen in the 2026-11-03 live test).
  const decideCode = (
    decide.error as { response?: { data?: { error?: { code?: string } } } } | null
  )?.response?.data?.error?.code;
  const wrongAccount =
    decideCode === 'SUPER_ADMIN_TOKENS_FORBIDDEN' || decideCode === 'GHOST_CANNOT_APPROVE';
  async function switchAccount() {
    await logout();
    navigate('/login', { replace: true, state: { from: returnTo } });
  }

  if (!_hasHydrated) {
    return (
      <Shell>
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-500" />
      </Shell>
    );
  }

  // No code in the URL: let the customer type the one their tool shows.
  if (!code) {
    return (
      <Shell>
        <h1 className="mb-2 text-center text-2xl font-bold">Connect a tool</h1>
        <p className="text-muted-foreground mb-6 text-center text-sm">
          Enter the code shown by the app that asked to connect to your Tyashin account.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const clean = typed.trim().toUpperCase();
            if (clean) navigate(`/device?code=${encodeURIComponent(clean)}`);
          }}
          className="space-y-4"
        >
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="ABCD-EFGH"
            autoFocus
            className="w-full rounded-lg border px-4 py-3 text-center font-mono text-xl uppercase tracking-widest dark:bg-gray-800"
          />
          <button
            type="submit"
            className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            Continue
          </button>
        </form>
      </Shell>
    );
  }

  if (!isAuthenticated) {
    return (
      <Shell>
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
            <KeyRound className="h-7 w-7 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold">Connect a tool to Tyashin</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Sign in, or create a free account, to let the app showing code{' '}
            <span className="font-mono font-semibold">{code}</span> build and manage your website.
          </p>
        </div>
        <div className="space-y-3">
          <button
            onClick={() => navigate('/login', { state: { from: returnTo } })}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            <LogIn className="h-4 w-4" /> Sign in
          </button>
          <button
            onClick={() => navigate('/register', { state: { from: returnTo } })}
            className="flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <UserPlus className="h-4 w-4" /> Create a free account
          </button>
        </div>
      </Shell>
    );
  }

  if (decide.data) {
    const approved = decide.data.status === 'approved';
    return (
      <Shell>
        <div className="text-center">
          {approved ? (
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-600" />
          ) : (
            <ShieldX className="mx-auto mb-4 h-12 w-12 text-gray-500" />
          )}
          <h1 className="mb-2 text-xl font-bold">{approved ? 'Connected' : 'Request denied'}</h1>
          <p className="text-muted-foreground mb-6 text-sm">
            {approved
              ? `${decide.data.clientName} can now work on your website. You can close this tab and return to it. Revoke access any time under Settings → Connected tools.`
              : 'Nothing was connected. You can close this tab.'}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700"
          >
            Go to dashboard
          </button>
        </div>
      </Shell>
    );
  }

  if (request.isPending) {
    return (
      <Shell>
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-500" />
      </Shell>
    );
  }

  if (request.isError) {
    return (
      <Shell>
        <div className="text-center">
          <ShieldX className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <h1 className="mb-2 text-xl font-bold">Code not found</h1>
          <p className="text-muted-foreground mb-6 text-sm">
            {apiErrorMessage(
              request.error,
              'This code has expired or was already used. Start again from your tool.'
            )}
          </p>
          <button
            onClick={() => navigate('/device')}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700"
          >
            Enter a different code
          </button>
        </div>
      </Shell>
    );
  }

  const req = request.data;
  return (
    <Shell>
      <div className="mb-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
          <KeyRound className="h-7 w-7 text-blue-600 dark:text-blue-400" />
        </div>
        <h1 className="text-2xl font-bold">Allow access?</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          <span className="text-foreground font-semibold">{req.clientName}</span> wants to build and
          manage the website on <span className="text-foreground font-semibold">{user?.email}</span>
          .
        </p>
      </div>
      <div className="mb-5 rounded-lg border bg-gray-50 p-4 text-center dark:bg-gray-800">
        <div className="text-muted-foreground mb-1 text-xs">Check this matches your tool</div>
        <div className="font-mono text-2xl font-semibold tracking-widest">{req.userCode}</div>
      </div>
      <ul className="text-muted-foreground mb-6 list-disc space-y-1 pl-5 text-sm">
        <li>It can create and update your website and its settings.</li>
        <li>It cannot buy anything, change billing, or manage your team.</li>
        <li>Access lasts 90 days and can be revoked any time in Settings.</li>
      </ul>
      {decide.isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20">
          {apiErrorMessage(decide.error, 'Could not complete the request')}
          {wrongAccount && (
            <button
              onClick={switchAccount}
              className="mt-2 block font-medium text-red-700 underline dark:text-red-300"
            >
              Use a different account
            </button>
          )}
        </div>
      )}
      <div className="flex gap-3">
        <button
          onClick={() => decide.mutate(false)}
          disabled={decide.isPending}
          className="flex-1 rounded-lg border px-4 py-2.5 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 dark:hover:bg-gray-800"
        >
          Deny
        </button>
        <button
          onClick={() => decide.mutate(true)}
          disabled={decide.isPending}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {decide.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Allow
        </button>
      </div>
    </Shell>
  );
}
